import React from 'react';

import classNames from 'classnames';
import NumberFormat from 'react-number-format';

import { formatCommittingQty, formatFigure, formatMoneyWithLocale } from 'Helpers/formatService';

import EditIcon from 'Assets/icons/edit.svg';

const moneyFigure = (value) => formatFigure(value, formatMoneyWithLocale, { precision: 3 });

/**
 * RevenueCommitmentLineTable
 *
 * The single pre-loaded commitment line of the Commit Revenue screen — the revenue sibling of
 * TicketingCommitmentLinesTable, sharing its blue header.
 *
 * Revenue keeps its quantity x price model, so there are two editable figures rather than one,
 * and the resulting value is shown alongside them and recalculates live. Replaces the old
 * click-row-to-open-a-modal flow.
 *
 * Props:
 *  - line:            { name, uncommittedQuantity }
 *  - quantity, price: raw input strings, so a half-typed value survives editing
 *  - quantityInvalid, priceInvalid
 *  - committingValue: quantity x price, computed by the parent so every surface agrees
 *  - onQuantityChange, onPriceChange
 */
export default function RevenueCommitmentLineTable({
    line = {},
    quantity = '',
    price = '',
    quantityInvalid = false,
    quantityOverCommitted = false,
    priceInvalid = false,
    committingValue = 0,
    onQuantityChange,
    onPriceChange,
}) {
    // Written out in full rather than interpolated: Tailwind only emits classes it can see
    // literally in the source, so a computed `tw-text-${align}` would not be generated.
    const headerCell = (label, alignEnd = false) => (
        <th className={classNames('tw-text-xs tw-font-bold tw-text-charcoal tw-uppercase tw-tracking-wide tw-p-3', alignEnd ? 'tw-text-end' : 'tw-text-start')}>
            {label}
        </th>
    );

    // Three states, because over-commit is permitted rather than invalid: solid red rejects the
    // value, muted red flags it as accepted-but-negative, blue is ordinary. aria-invalid is set
    // only for a true rejection so assistive tech does not announce a permitted value as an error.
    const numberInput = (value, { invalid = false, warning = false }, ariaLabel, onChange) => (
        <div
            className={classNames('tw-inline-flex tw-items-center tw-gap-x-2 tw-rounded-lg tw-border tw-px-2 tw-py-1', {
                'tw-border-red tw-bg-red-10': invalid,
                'tw-border-red-50 tw-bg-red-10': !invalid && warning,
                'tw-border-blue tw-bg-blue-10': !invalid && !warning,
            })}
        >
            {/* NumberFormat, not a bare number input: a number input cannot carry thousands
                separators, which left the figures being typed as the only unseparated numbers on the
                screen. onValueChange hands the parent the UNformatted digits, so its arithmetic is
                unaffected. text-sm AND font-medium match the read-only cells beside it, per Dave's
                instruction that the committing inputs match the typography used elsewhere on the
                screen. The box, tint and pencil are the wireframe's, and stay. */}
            <NumberFormat
                value={value}
                thousandSeparator={true}
                decimalScale={2}
                allowNegative={false}
                inputMode="decimal"
                aria-label={ariaLabel}
                aria-invalid={invalid || undefined}
                onValueChange={({ value: raw }) => onChange && onChange(raw)}
                className="tw-w-24 tw-border-0 tw-bg-transparent tw-p-0 tw-text-end tw-text-sm tw-font-medium tw-tabular-nums tw-text-charcoal focus:tw-outline-none"
            />
            <EditIcon className="tw-w-3.5 tw-h-3.5 tw-shrink-0 tw-text-blue" />
        </div>
    );

    return (
        <div className="tw-rounded-xl tw-border tw-border-charcoal-15 tw-overflow-hidden tw-shadow-sm">
            <table className="tw-w-full inbud-table tw-text-sm">
                <thead>
                    <tr className="tw-bg-blue-25">
                        {headerCell($t('Budget line'))}
                        {headerCell($t('Uncommitted Qty'), true)}
                        {headerCell($t('Committing Qty'), true)}
                        {headerCell($t('Price'), true)}
                        {headerCell($t('Committing value'), true)}
                    </tr>
                </thead>
                <tbody>
                    <tr className="tw-border-b tw-border-charcoal-15">
                        <td className="tw-p-3 tw-text-charcoal tw-font-semibold">{line.name || '-'}</td>
                        <td className="tw-p-3 tw-text-end tw-tabular-nums tw-text-charcoal tw-font-medium">
                            {formatFigure(line.uncommittedQuantity, formatCommittingQty, { precision: 1 })}
                        </td>
                        <td className="tw-p-3 tw-text-end">
                            {numberInput(quantity, { invalid: quantityInvalid, warning: quantityOverCommitted }, $t('Committing Qty'), onQuantityChange)}
                        </td>
                        <td className="tw-p-3 tw-text-end">{numberInput(price, { invalid: priceInvalid }, $t('Price'), onPriceChange)}</td>
                        <td className="tw-p-3 tw-text-end tw-tabular-nums tw-text-charcoal tw-font-bold">{moneyFigure(committingValue)}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}
